import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import { DomainModel } from '@/models/proficiency/domain';
import { CompetencyModel } from '@/models/proficiency/competency';
import { TxGradeBoundary } from '@/models/taxonomy/grade-boundary';
import { GradeModel } from '@/models/taxonomy/grade';
import { SubjectModel } from '@/models/taxonomy/subject';
import { competencyAPI, CompetencyAPI } from '@/providers/apis/competency/competency';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import axios from 'axios';
import * as d3 from 'd3';
import moment from 'moment';

import { Domain } from '@/models/proficiency/domain-topic/domain';
import { Topic } from '@/models/proficiency/domain-topic/topic';
import { Competency } from '@/models/proficiency/domain-topic/competency';
import { competencySerializer } from '@/providers/serializers/competency/competency';
import { consoleSerializer } from '@/providers/serializers/console/console';
import { RGCompetencyModel, RGDomainModel, RGSubjectModel, RGTopicModel } from '@/models/proficiency/route-generator';
import { sortByProperty } from '@/utils/utils';
import { ROUTE_MAP_ICONS } from '@/utils/constants';
import { appConfigService } from '@/providers/services/app/app-config';
import MarkerListPanelComponent from '@/components/showcase/marker-list-panel/marker-list-panel';
import { markersAPI } from '@/providers/apis/markers/markers';
import { MarkerListModel } from '@/models/marker/marker-list';
import { markerSerilaizer } from '@/providers/serializers/markers/markers';

@Component({
  name: 'learner-proficiency-chart',
  components: {
    'google-material-icon': GoogleMaterialIcon,
    'marker-card': MarkerListPanelComponent,
  },
})

export default class LearnerProficiencyChart extends Vue {

  get chartHeight() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
    const chartContainerHeight = chartContainer.offsetHeight;
    const chartHeight = component.isShowExpandedGraph ?
      component.highestTopicSize * component.expandedGraphCellHeight :
      chartContainerHeight;
    return chartContainerHeight || chartHeight - 15;
  }

  get chartWidth() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
    return chartContainer.offsetWidth - 30;
  }

  get subjectCode() {
    return this.subject.code;
  }

  get subjectClassCode() {
    const subjectCode = this.subject.code;
    return subjectCode.replaceAll(/\./g, '-');
  }

  private get maxGradeNumber() {
    return this.activeGradeList.reduce(
      (prev: any, current: any) => (prev.id > current.id) ?
        prev : current);
  }

  private get isDemoTenant() {
    return appConfigService.isDemoTenant();
  }

  private get showMarker() {
    return this.enableMarker && this.isShowCase;
  }

  private get showEditView() {
    return this.enableShowCase;
  }

  @Prop({default: true})
  public enableMarker!: boolean;

  @Prop()
  public enableShowCase!: boolean;

  @Prop()
  private isShowCase!: boolean;

  @Prop()
  private userId!: string;

  @Prop()
  private subject!: SubjectModel;

  @Prop()
  private showTabIndex!: any;

  private proficiencyChartContainer!: any;

  private domainCompetencyMatrix!: any[];

  private domainTopicMetadata: any[] = [];

  private chartData?: any = [];

  private skylineContainer!: any;

  private gradelineContainer!: any;

  private proficiencyChartWidth: number = 400;

  private proficiencyChartHeight: number = 600;

  private expandedGraphCellWidth: number = 40;

  private expandedGraphCellHeight: number = 40;

  private topicCellHeight: number = 40;

  private compressedGraphCellWidth: number = 40;

  private compressedGraphCellHeight: number = 10;

  private routeContainer?: any;

  private cellWidth: number = 40;

  private cellHeight: number = 10;

  @Prop()
  private activeCompetency!: any;

  private maxDomainSize: number = 0;

  private isShowExpandedGraph: boolean = false;

  private taxonomyGrades: GradeModel[] = [];

  private gradeBoundaries!: TxGradeBoundary[];

  private activeGrade: any = {};

  private isLoading: boolean = false;

  private numberOfTopics = 1;

  @Prop()
  private isDomainActive?: any;

  @Prop()
  private isGradeSelect?: boolean;

  @Prop()
  private isCompetencyActive: boolean = false;

  @Prop()
  private month?: string;

  @Prop()
  private year?: string;

  private totalCompetencies: number = 0;

  @Prop()
  private activeDomainSeq?: any;

  @Prop()
  private activeDomain: Domain | any;

  @Prop()
  private activeTopic: Topic | null = null;

  @Prop()
  private isCompetencyLoading: any;

  private activeCompetencyStyle: any = null;

  @Prop()
  private isCompetencyMap!: boolean;

  @Prop()
  private isDomainView?: boolean;

  private multiGradeActiveList: any = [];

  private activeGradeList: any = [];

  @Prop()
  private prerequisites: any;

  @Prop()
  private isCompetencyView!: boolean;

  private isSelectedCompetency: boolean = false;

  @Prop()
  private selectedDomain: any;

  @Prop()
  private selectedCompetency: any;

  @Prop()
  private isDomainPanelEnabled: any;

  private isToggleGrade: boolean = false;

  private minGradeNumber!: any;

  private selectedGradeCompetency: any = [];

  private skylinePoints: any = [];

  private isExpanding: boolean = false;

  private highestTopicSize: number = 0;

  private fetchedGradeBoundaries: Array<{
    grade: number,
    boundaries: TxGradeBoundary[],
    isActive: boolean,
    boundaryPoints: any[],
  }> = [];

  private gradesBoundaryPoints: any[] = [];

  private isCloseOtherDomain: boolean = false;

  private isShowRouteLine: boolean = false;

  private rgDomains: RGDomainModel[] = [];

  private crossWalkData: any = [];

  private markerBoundaries: any = [];

  @Prop()
  private learner: any;

  private competencyHoverData: any = {
    isShow: false,
    pageX: 0,
    pageY: 0,
    data: null,
  };

  @Prop()
  private enableRouteLine!: boolean;

  @Prop()
  private isCardView!: boolean;

  @Prop()
  private activeMarker!: any;

  @Watch('enableRouteLine')
  public onWatchRouteLine(val: boolean) {
    if (val) {
      this.drawRouteLine(this.rgDomains);
    } else {
      this.resetRouteLines();
    }
  }

  @Watch('selectedDomain')
  public onChangeSelectedDomain(selectedDomain: any) {
    this.toggleActiveDomainBar(0, false);
    if (selectedDomain) {
      // this.onSelectDomain(selectedDomain);
    }
  }

  @Watch('selectedCompetency')
  public onChangeSelectedCompetency(competency: any) {
    if (!this.isShowExpandedGraph) {
      this.isShowExpandedGraph = true;
    }
    this.selectCompetency(competency);
    this.isSelectedCompetency = true;
  }

  @Watch('isCompetencyView')
  public onChangeCompetencyView(value: boolean) {
    if (!value) {
      this.isSelectedCompetency = false;
      this.drawDomainTopicCompetencyChart();
    }
  }

  @Watch('isDomainView')
  public watchCompetencyDomain(value: any) {
    if (value) {
      this.activeGrade = {};
      this.activeGradeList = [];
      this.resetChart();
      this.drawDomainTopicCompetencyChart();
      if (this.selectedDomain.domainSeq) {
        this.toggleActiveDomainBar(this.selectedDomain.domainSeq);
      }
    }
  }

  @Watch('isCompetencyActive')
  public watchCompetencyClose(value: boolean) {
      if (!value && this.activeDomain) {
        this.resetRouteLines();
        d3.select(`#domain-group-${this.activeDomain.domainSeq}-${this.subjectClassCode} #active-competency`).remove();
      }
  }

  @Watch('subjectCode')
  public onChangeSubject() {
    const component = this;
    component.loadChartData();
    component.loadTaxonomyGrades();
    component.activeGrade = {};
  }

  @Watch('activeMarker')
  public onChangeMarker() {
    this.fetchedGradeBoundaries = [];
    this.loadMarkerBoundaries(this.chartData);
  }

  public created() {
    const queryParams = {
      user: this.userId,
      subject: this.subjectCode,
    };
    this.loadTaxonomyGrades();
  }


  // -------------------------------------------------------------------------
  // Actions

  public onToggleGraphView() {
    const component = this;
    component.isShowExpandedGraph = !component.isShowExpandedGraph;
    component.isSelectedCompetency = false;
    this.skylinePoints.map((point: any) => point.isExpanded = component.isShowExpandedGraph);
    this.chartData.map((domain: Domain) => domain.isExpanded = component.isShowExpandedGraph);
    this.drawDomainTopicCompetencyChart();
    this.handleDomainGradeTransition(undefined, component.isShowExpandedGraph);
    if (component.isCompetencyMap) {
      component.$emit('onChangeGraphView', component.isShowExpandedGraph);
      if (this.selectedDomain.domainSeq && this.isDomainView) {
        component.toggleActiveDomainBar(component.selectedDomain.domainSeq);
      }
    }
    if (component.activeDomainSeq) {
      component.toggleActiveDomainBar(component.activeDomainSeq);
    }
  }

  public onSelectGrade(grade: GradeModel | any) {
    const component = this;
    component.multiGradeActiveList = [];
    component.selectedGradeCompetency = [];
    if (!this.isCompetencyMap) {
      this.activeGradeList = [];
      this.taxonomyGrades.map((taxonomyGrade) => taxonomyGrade.isActive = taxonomyGrade.id === grade.id);
      this.fetchedGradeBoundaries.map(
        (gradeBoundaries) => gradeBoundaries.isActive = gradeBoundaries.grade === grade.id);
    }

    if (this.activeGradeList.includes(grade)) {
      this.activeGradeList.splice(this.activeGradeList.findIndex(
          (activeGrade: GradeModel) => activeGrade.id === grade.id));
      grade.isActive = false;
    } else {
      this.activeGradeList.push(grade);
      grade.isActive = true;
    }
    this.loadTaxonomyGradeBoundaries(grade.id);
  }

  public parseBoundaryCompetency() {
    const component = this;
    if (component.activeGradeList.length) {
      // Note : We Currently use only Hi line so we use first object
      const hiLineGrade = [component.activeGradeList[0]];
      const boundaryPromise = hiLineGrade.map((gradeItem: any, gradeIndex: number) => {
        return new Promise((resovle) => {
          (component.loadTaxonomyGradeBoundaries(gradeItem.id) as any).then((boundary: any) => {
            component.multiGradeActiveList[gradeItem.id] = boundary;
            component.gradeBoundaries = boundary;
            return resovle(boundary);
          });
        });
      });
      Promise.all(boundaryPromise).then(() => {
        component.parseGradeBoundaryChartData();
        if (this.isCompetencyMap) {
          this.$emit('onSelectGrade',
            this.selectedGradeCompetency, component.activeGradeList);
        }
      });
    } else {
      d3.select(this.$el).selectAll('.gradeline-group line').remove();
      d3.select(this.$el).selectAll('#cells-group rect').classed('no-competency', false);
    }
  }

  public onSelectDomainDEPRECATED(domain: DomainModel) {
    const component = this;
    if (!domain.competencies) {
      domain = this.chartData.find((domainChart: DomainModel) => {
        return domainChart.domainCode === domain.domainCode;
      });
    }
    this.isLoading = false;
    if (this.isCompetencyMap) {
      this.chartData.map((domainItem: any) => {
        this.$set(domainItem, 'isExpanded', false);
      });
      this.$set(domain, 'isExpanded', true);
    }
    this.$emit('onSelectDomain', domain);
    component.toggleActiveDomainBar(domain.domainSeq);
  }

  public onToggleDomainView(domain: Domain) {
    this.isExpanding = true;
    if (domain && domain.domainSeq) {
      this.handleDomainBarTransition(domain, domain.domainSeq, !domain.isExpanded);
      // Expanded flag is set while toggling chart size
      // So, we have to check isExpanded === true for drawing topic chart
      if (domain.isExpanded) {
        this.drawTopicChart(domain, domain.domainSeq);
      }

    }
    this.isExpanding = false;
  }

  public onExpandDomain(domain: Domain) {
    this.isExpanding = true;
    if (domain && domain.domainSeq) {
      // NOTE
      // Send event to parent component and watchActiveDomain will be triggered
      // while setting active domain data

      this.$emit('onSelectDomain', domain);
    }
    this.isExpanding = false;
  }

  public async onCloseDomain(domain: Domain) {
    this.isExpanding = true;
    if (domain && domain.domainSeq) {
      d3.select(`#domain-group-${domain.domainSeq}-${this.subjectClassCode} #active-competency`).remove();
      await this.handleDomainBarTransition(domain, domain.domainSeq, !domain.isExpanded);
      await this.handleDomainGradeTransition(domain.domainSeq, false);
      await this.$emit('onCloseDomain', domain);
      if (this.isShowRouteLine && this.isDemoTenant) {
        this.drawRouteLine(this.rgDomains);
      }
    }
    // TODO it required while enable single domain close in chart expanded mode is enabled
    // if (!this.$el.querySelector(`#domains-group #domain-group-${domain.domainSeq} #competencies-group rect`)) {
    //   this.isShowExpandedGraph = false;
    //   this.drawDomainChart(domain, domain.domainSeq);
    // }
    this.isExpanding = false;
    if (!this.chartData.find((item: any) => item.isExpanded)) {
      this.resetRouteLines();
    }
  }

  public onSelectTopic(topic: Topic, domainSeq: number) {
    const domainTopics = [...document.querySelectorAll(`#domain-group-${domainSeq}-${this.subjectClassCode} .topic-group [class*="topic-"]`)];
    this.$emit('onSelectTopic', topic);
  }

  public backAction() {
    this.$emit('backAction');
  }

  public loadChartData() {
    const component = this;
    component.isLoading = true;
    axios.all([
      component.fetchUserDomainCompetencyMatrix(),
      component.getSubjectDomainTopicMetadata(),
    component.fetchCrosswalk()]).then(axios.spread(
        (domainCompetencyMatrix: Domain[], domainTopicMetadata: Domain[], crossWalk: any) => {
          component.crossWalkData = crossWalk;
          component.domainTopicMetadata = domainTopicMetadata;
          component.domainCompetencyMatrix = domainCompetencyMatrix;
          component.parseDomainTopicCompetencyMatrix(domainCompetencyMatrix, domainTopicMetadata);
          component.isLoading = false;
          if (this.isCompetencyMap) {
            this.$emit('loadCompetencyMap', component.chartData);
          }
        }));
  }

  public loadTaxonomyGrades() {
    const component = this;
    return taxonomyAPI.fetchTaxonomyGrades(this.subjectCode).then((taxonomyGrades: GradeModel[]) => {
      component.taxonomyGrades = taxonomyGrades.sort((a: any, b: any) => a.sequence - b.sequence);
      component.loadChartData();
    });
  }

  public loadTaxonomyGradeBoundaries(gradeId: number) {
    const component = this;

    this.fetchedGradeBoundaries.map((gradeBoundariesItem) => gradeBoundariesItem.isActive = false);
    const gradeBoundaries = this.fetchedGradeBoundaries.find(
        (gradeBoundariesItem) => gradeBoundariesItem.grade === gradeId);
    if (!gradeBoundaries) {
      return taxonomyAPI.fetchTaxonomyGradeBoundaries(gradeId).then((gradeBoundariesItem: TxGradeBoundary[]) => {
        component.fetchedGradeBoundaries.push({
          grade: gradeId,
          boundaries: gradeBoundariesItem,
          isActive: true,
          boundaryPoints: component.getGradeBoundaryPoints(gradeBoundariesItem),
        });
        return this.drawGradeBoundaryLine();
      });
    } else {
      gradeBoundaries.isActive = true;
      return this.drawGradeBoundaryLine();
    }
  }


  public parseChartData(domainTopicMetadata: any, domainCompetencyMatrix: any) {
    const component = this;
    const chartData: any = [];
    let maxDomainSize = 0;
    let totalCompetencies = 0;
    domainTopicMetadata.map((domainCoOrdinate: any) => {
      const domainMatrixData = domainCompetencyMatrix.find(
        (domainMatrix: any) => domainMatrix.domainCode === domainCoOrdinate.domainCode,
      );
      const competencies = domainMatrixData.competencies;
      const domainCode = domainMatrixData.domainCode;
      const domainName = domainCoOrdinate.domainName;
      const domainSeq = domainCoOrdinate.domainSeq;
      const domainChartData: any = {
        domainName,
        domainCode,
        domainSeq,
        competencies: [],
      };
      maxDomainSize = competencies.length > maxDomainSize ? competencies.length : maxDomainSize;
      totalCompetencies += competencies.length;
      competencies.map((competency: CompetencyModel) => {
        const competencyData = {
          domainName,
          domainCode,
          domainSeq,
          competencyCode: competency.competencyCode,
          competencyName: competency.competencyName,
          competencySeq: competency.competencySeq,
          competencyStudentDesc: competency.competencyStudentDesc,
          competencyStatus: component.isCompetencyMap ? 2 : competency.status,
          isMastered: competency.status > 1,
          isInferred: competency.status === 2 || competency.status === 3,
          isSkyLineCompetency: false,
          isGradeBoundary: false,
        };
        domainChartData.competencies.push(competencyData);
      });
      const masteredCompetencies = domainChartData.competencies.filter(
        (domainCompetency: any) => domainCompetency.isMastered,
      );
      const skylineCompetencyPos = masteredCompetencies.length === 0 ? 0 : masteredCompetencies.length - 1;
      const skylineCompetency = domainChartData.competencies[skylineCompetencyPos];
      skylineCompetency.isSkyLineCompetency = true;
      chartData.push(domainChartData);
    });
    component.maxDomainSize = maxDomainSize;
    component.totalCompetencies = totalCompetencies;
    return chartData;
  }

  public parseGradeBoundaryChartData() {
    const component = this;
    const proficiencyChartData = component.chartData;
    if (this.multiGradeActiveList.length) {
      const boundaryList = component.multiGradeActiveList;
      const multiGradePromise = boundaryList.map((gradeBoundaries: any, gradeIndex: any) => {
        return new Promise((resolve) => {
          return resolve(this.boundaryMapForMultiGrade(proficiencyChartData, gradeBoundaries, gradeIndex));
        });
      });
      Promise.all(multiGradePromise).then(() => {
        component.chartData = proficiencyChartData;
        component.drawProficiencyChart();
        boundaryList.map((grade: any, index: any) => {
          component.drawGradeBoundaryLine(index);
        });
        component.drawGradeBoundaryLine();
      });
    }
  }

  public boundaryMapForMultiGrade(proficiencyChartData: any, gradeBoundaries: any, gradeIndex: any = null) {
    if (gradeBoundaries && gradeBoundaries.length) {
      return proficiencyChartData.map((domainChartData: DomainModel) => {
        const competencies = domainChartData.competencies;
        const domainGradeBoundary: TxGradeBoundary | any = gradeBoundaries.find(
          (gradeBoundary: TxGradeBoundary) => gradeBoundary.domainCode === domainChartData.domainCode,
        );
        let gradeBoundaryCompetency: any = competencies[0];
        if (domainGradeBoundary) {
          gradeBoundaryCompetency = competencies.find(
            (competency: any) => competency.competencyCode === domainGradeBoundary.highline,
          );
          if (gradeIndex) {
            gradeBoundaryCompetency.isGradeBoundary = true;
          }
        } else {
          gradeBoundaryCompetency.isNoMapping = true;
        }
        if (!gradeBoundaryCompetency.className ||
          (gradeBoundaryCompetency.className &&
            gradeBoundaryCompetency.className
              .indexOf(`grade-boundary-competency-${gradeIndex}`) === -1) && gradeIndex) {
          if (gradeIndex) {
            gradeBoundaryCompetency.className += ` grade-boundary-competency-${gradeIndex}`;
          } else {
            gradeBoundaryCompetency.isLowline = true;
            gradeBoundaryCompetency.className += ` grade-lo-line-competency`;
          }
        }
      });
    }
  }


  public drawDomainChart(domainChartData: any, domainSeq: number) {
    const component = this;
    const svg = d3.select(this.$el).select('.domains-group');
    const cellWidth = component.cellWidth;
    const cellHeight = component.cellHeight;
    let competencySeq = -1;
    const domainGroup = svg.append('g').attr('id', `domain-group-${domainSeq}-${this.subjectClassCode}`);
    domainGroup.attr('transform', `translate(${cellWidth * (domainSeq - 1)}, 0)`);
    const domainCompetencyGroup = domainGroup.append('g').attr('class', 'competencies-group')
      .on('click', async () => {
        const hasRGDomain = this.rgDomains.find(
            (rgDomain) => this.isShowRouteLine && rgDomain.domainCode === domainChartData.domainCode);
        const activeDomains = this.chartData.filter((item: any) => item.isExpanded && !hasRGDomain);
        if (!hasRGDomain) {
          this.resetRouteLines();
        }
        for await (const domain of activeDomains) {
          await this.clearExisitingDomain(domain);
        }
        this.isCloseOtherDomain = false;
        setTimeout(() => {
          this.$emit('onSelectDomain', domainChartData);
        }, 50);
        })
        .on('keypress', async () => {
          const hasRGDomain = this.rgDomains.find(
              (rgDomain) => this.isShowRouteLine && rgDomain.domainCode === domainChartData.domainCode);
          const activeDomains = this.chartData.filter((item: any) => item.isExpanded && !hasRGDomain);
          if (!hasRGDomain) {
            this.resetRouteLines();
          }
          for await (const domain of activeDomains) {
            await this.clearExisitingDomain(domain);
          }
          this.isCloseOtherDomain = false;
          setTimeout(() => {
            this.$emit('onSelectDomain', domainChartData);
          }, 50);
          });


    const groupedDomainCompetencies = new Array(domainChartData.totalCompetencies).fill({});
    const competencyCells = domainCompetencyGroup.selectAll('.competency').data(groupedDomainCompetencies);
    competencyCells
      .enter()
      .append('rect')
      .attr('class', (competency: any, index: number) => {
        const curPos = index + 1;
        const statusNumber = curPos <= domainChartData.masteredCompetencies ?
          '4' :
          curPos <= domainChartData.masteredCompetencies + domainChartData.inprogressCompetencies ?
            '1' :
            '0';
        return `competency-cell competency-${index + 1}
            ${this.isCompetencyMap ? ' competency-status-fill-5 ' : 'competency-status-fill-' + statusNumber}
            ${competency.isSkyLineCompetency ? 'skyline-competency' : ''}`;
      })
      .attr('width', cellWidth)
      .attr('height', cellHeight)
      .attr('tabindex', this.showTabIndex === false ? -1 : 0)
      .attr('role', 'link')
      .attr('aria-label', (competency: any, index: number) => {
        const curPos = index + 1;
        const statusNumber = curPos <= domainChartData.masteredCompetencies ?
          '4' :
          curPos <= domainChartData.masteredCompetencies + domainChartData.inprogressCompetencies ?
            '1' :
            '0';
        return `Competency ${statusNumber === '1' ? 'In Progress' : (statusNumber === '0' ? 'Not started' : 'Mastered')}`;
      })
      .attr('x', 0)
      .attr('y', (competency: any, index: number) => {
        competencySeq++;
        return competencySeq * cellHeight;
      });
    competencyCells.exit().remove();

  }

  public getTranslation(transform: any) {
    // Create a dummy g for calculation purposes only. This will never
    // be appended to the DOM and will be discarded once this function
    // returns.
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    // Set the transform attribute to the provided string value.
    g.setAttributeNS(null, 'transform', transform);

    // consolidate the SVGTransformList containing all transformations
    // to a single SVGTransform of type SVG_TRANSFORM_MATRIX and get
    // its SVGMatrix.
    const matrix = (g.transform.baseVal.consolidate() as any).matrix;

    // As per definition values e and f are the ones for the translation.
    return [matrix.e, matrix.f];
  }

  public getHorizontalLinePoints(x1: number, y1: number, alignmentPixes: number = 0) {
    const component = this;
    const cellHeight = component.cellHeight;
    const cellWidth = component.cellWidth;
    y1 = y1 === 0 ? y1 : y1 + cellHeight;
    const x2 = x1 + cellWidth;
    const y2 = y1;
    return {
      x1,
      y1,
      x2,
      y2,
    };
  }

  /**
   * @function joinSkyLinePoints
   * Method to draw vertical line to connects sky line points, if necessary
   */
  public joinSkylineVerticalPoints(skylineStartPos: number, skylineEndPoint: any) {
    const component = this;
    const skylineStartElement: any = this.$el.querySelector(`.skyline-group .line-${skylineStartPos - 1}`);
    if (skylineStartElement) {
      const skyLineContainer = component.skylineContainer;
      const skylineStartPoint: any = {
        x2: parseInt(skylineStartElement.getAttribute('x2'), 10),
        y2: parseInt(skylineStartElement.getAttribute('y2'), 10),
      };
      // Connect sky line points if last and current points are not same
      if (
        skylineStartPoint.y2 !== skylineEndPoint.y1
      ) {
        // Increase extra height to connect intersection points
        if (skylineStartPoint.y2 > skylineEndPoint.y1) {
          skylineStartPoint.y2 = skylineStartPoint.y2 + 3;
          skylineEndPoint.y1 = skylineEndPoint.y1 - 3;
        } else {
          skylineStartPoint.y2 = skylineStartPoint.y2 - 3;
          skylineEndPoint.y1 = skylineEndPoint.y1 + 3;
        }

        skyLineContainer
          .append('line')
          .attr('x1', skylineStartPoint.x2)
          .attr('y1', skylineStartPoint.y2)
          .attr('x2', skylineEndPoint.x1)
          .attr('y2', skylineEndPoint.y1)
          .attr('class', `skyline connect-line-${skylineStartPos - 1}-line-${skylineStartPos}`);
      }
    }
  }

  /**
   * @function joinSkyLinePoints
   * Method to draw vertical line to connects sky line points, if necessary
   */
  public joinGradelineVerticalPoints(skylineStartPos: number, skylineEndPoint: any, gradeIndex: any) {
    const component = this;
    const skylineStartElement: any | void = this.$el.querySelector(
      `.gradeline-group .line-${gradeIndex ? gradeIndex : 'lo-grade'}-${skylineStartPos - 1}`);
    if (skylineStartElement) {
      const skyLineContainer = component.gradelineContainer;
      const skylineStartPoint: any | void = {
        x2: parseInt(skylineStartElement.getAttribute('x2'), 10),
        y2: parseInt(skylineStartElement.getAttribute('y2'), 10),
      };
      // Connect sky line points if last and current points are not same
      if (
        skylineStartPoint.y2 !== skylineEndPoint.y1
      ) {
        // Increase extra height to connect intersection points
        if (skylineStartPoint.y2 > skylineEndPoint.y1) {
          skylineStartPoint.y2 = skylineStartPoint.y2 + 2;
          skylineEndPoint.y1 = skylineEndPoint.y1 - 2;
        } else {
          skylineStartPoint.y2 = skylineStartPoint.y2 - 2;
          skylineEndPoint.y1 = skylineEndPoint.y1 + 2;
        }

        skyLineContainer
          .append('line')
          .attr('x1', skylineStartPoint.x2)
          .attr('y1', skylineStartPoint.y2)
          .attr('x2', skylineEndPoint.x1)
          .attr('y2', skylineEndPoint.y1)
          .attr('class', `connect-line-${skylineStartPos - 1}-line-${skylineStartPos}
                       ${this.maxGradeClass(gradeIndex)}`);
      }
    }
  }

  /**
   * @function showDropShadow
   * Method to show a drop shadow in skyline
   */
  public addSkylineBackshadow() {
    const component = this;
    const skylineContainer = component.skylineContainer;
    const filterContainer = skylineContainer
      .append('defs')
      .append('filter')
      .attr('class', 'back-shadow');
    filterContainer
      .append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '4');
  }

  public fetchUserDomainCompetencyMatrix() {
    const month = Number(this.month);
    const year = Number(this.year);
    const params = {
      user: this.userId,
      subject: this.subjectCode,
      month,
      year,
    };
    // return competencyAPI.fetchUserDomainCompetencyMatrix(params);
    return competencyAPI.fetchDomainTopicCompetencyMatrix(params);
  }

  public getSubjectDomainTopicMetadata() {
    const params = {
      subject: this.subjectCode,
    };
    // return competencyAPI.getCompetencyMatrixCoordinates(params);
    return competencyAPI.fetchSubjectDomainTopicMetadata(params);
  }

  public selectCompetency(competency: any) {
    const component = this;
    this.isLoading = true;
    if (component.isShowExpandedGraph) {
      this.$emit('onSelectCompetency', competency);
    } else {
      const activeDomain = component.chartData.find((domain: DomainModel) => {
        return domain.domainCode === competency.domainCode;
      });
      // component.onSelectDomain(activeDomain);
    }
    if (!this.isCompetencyMap) {
      // component.highlightCompetency(competency);
    } else {
      if (!this.isGradeSelect) {
        component.activeGradeList = [];
      }
      component.resetChart();
    }
  }

  public drawProficiencyChart() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartHeight = component.chartHeight;
    const chartWidth = component.chartWidth;
    component.cellHeight = this.isShowExpandedGraph ? 40 : chartHeight / component.maxDomainSize;
    component.cellWidth = (chartWidth / proficiencyChartData.length) > 200
      ? 200 : (chartWidth / proficiencyChartData.length);
    d3.select('svg.chart-graph').remove();
    const svg = d3.select('.chart-area')
      .append('svg')
      .attr('class', 'chart-graph')
      .attr('width', component.cellWidth * proficiencyChartData.length)
      .attr('height', chartHeight);
    svg.append('g').attr('id', 'cells-group');
    component.skylineContainer = svg.append('g').attr('class', 'skyline-group');
    component.gradelineContainer = svg.append('g').attr('class', 'gradeline-group');
    component.routeContainer = svg.append('g').attr('class', 'prerequisite-group');
    component.proficiencyChartContainer = svg;
    proficiencyChartData.map((domainChartData: any) => {
      // component.drawDomainChart(domainChartData);
    });
    if (!this.isCompetencyMap) {
      component.drawProficiencySkyline();
    }
    if (!this.isSelectedCompetency) {
      const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
      chartContainer.scrollTop = chartHeight;
    }
    component.isLoading = false;
  }


  public drawGradeBoundaryLine(gradeIndex: any = null) {
    const component = this;
    const cellWidth = component.cellWidth;
    const cellHeight = component.cellHeight;

    let curBarPos = 0;
    let strokeDash = 0;
    let adjustableWidth = 10;
    const activatedGradeBoundaries = this.fetchedGradeBoundaries.filter((gradeBoundaries) => gradeBoundaries.isActive);

    if (!this.isCompetencyMap || !activatedGradeBoundaries.length) {  // draw only single line
      d3.select(this.$el).select(`.gradeline-group g`).remove();
    }
    activatedGradeBoundaries.forEach((gradeBoundaryPoints) => {
      d3.select(this.$el).select(`.gradeline-group grade-${gradeBoundaryPoints.grade}`).remove();
      let points = '';
      gradeBoundaryPoints.boundaryPoints.forEach((domain: any) => {
        if (domain.isExpanded) {
          // Get topic bar competency pos
          adjustableWidth = 10;
          domain.topics.map((topic: any, index: number) => {
            let end = 0;
            if (index === domain.topics.length - 1) {
              end = 10;
            }
            const x1 = curBarPos * this.cellWidth;
            const x2 = x1 + this.cellWidth;
            const y1 = topic.hiLineCompSeq * this.topicCellHeight;
            const y2 = y1;


            points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
            curBarPos++;
            strokeDash += (x1 === x2
              ? Math.max(y1, y2)
              : Math.max(x1, x2));

          });
        } else {
          const numOfTopics = domain.topics.reduce((a: any, b: any) => a + b.hiLineCompSeq, 0);
          const x1 = curBarPos * this.cellWidth;
          const x2 = x1 + this.cellWidth;
          const y1 = numOfTopics * this.cellHeight;
          const y2 = y1;
          strokeDash += (x1 === x2
            ? Math.max(y1, y2)
            : Math.max(x1, x2));
          points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
          curBarPos++;
        }

      });
      d3.select(this.$el).select('.gradeline-group')
        .append('g')
        .attr('id', `grade-${gradeBoundaryPoints.grade}`)
        .append('polyline')
        .attr('points', points)
        .attr('class', 'skyline')
        .attr('stroke-dasharray', strokeDash)
        .attr('stroke-dashoffset', strokeDash);
    });
    const gradeFilter = d3.select(this.$el).select('.gradeline-group').append('defs')
      .append('filter')
      .attr('class', 'back-shadow');
    gradeFilter.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '4');
  }

  private onShowMarker() {
    this.$emit('onShowMarker');
  }

  private onShowSelector() {
    this.isToggleGrade = !this.isToggleGrade;
  }

  private onGoBack() {
    this.onShowMarker();
  }

  private onSelectShowcase() {
    this.$emit('onSelectShowcase', this.activeCompetency);
  }

  private getGradeBoundaryPoints(gradeBoundaries: any[]) {
    const gradeBoundaryPoints: any[] = [];
    this.chartData.map((domainData: Domain) => {
      const domainGradeBoundariesList = gradeBoundaries.filter((item) => item.domainCode === domainData.domainCode);
      let domainHiLineCompSeq = 0;
      const domainTopicPoints = {
        domainSeq: domainData.domainSeq,
        topics: [] as any,
        isExpanded: !!domainData.isExpanded,
        isHiLineAvailable: !!domainGradeBoundariesList.length,
        hiLineCompSeq: domainHiLineCompSeq,
      };

      let isHiLineTopicCovered = false;
      let domainGradeBoundaries = gradeBoundaries.find((item) => item.domainCode === domainData.domainCode);
      domainData.topics.map((topic) => {
        const topicBoundaries = domainGradeBoundariesList.find(
          (item) => item.topicCode && item.topicCode === topic.topicCode,
        );
        if (topicBoundaries) {
          domainGradeBoundaries = topicBoundaries;
        }
        let hiLineCompSeq =
          !isHiLineTopicCovered &&
          domainGradeBoundaries &&
          !domainGradeBoundaries.topicHighlineComp &&
          topic.competencies
            ? topic.competencies.length
            : 0;
        const topicBoundary: any = {
          topicSeq: topic.topicSeq,
          hiLineCompSeq,
        };
        if (
          domainGradeBoundaries &&
          (topic.topicCode === domainGradeBoundaries.topicCode ||
            topic.topicCode === domainGradeBoundaries.highlineTopic)
        ) {
          isHiLineTopicCovered = true;
          if (topic.competencies) {
            hiLineCompSeq =
              topic.competencies.findIndex(
                (competency) =>
                  competency.competencyCode ===
                  (domainGradeBoundaries.topicHighlineComp
                    ? domainGradeBoundaries.topicHighlineComp
                    : domainGradeBoundaries.highlineComp),
              ) + 1;
          }
        }
        topicBoundary.hiLineCompSeq = hiLineCompSeq;
        domainTopicPoints.topics.push(topicBoundary);
        domainHiLineCompSeq += hiLineCompSeq;
      });

      domainTopicPoints.hiLineCompSeq = domainHiLineCompSeq;
      gradeBoundaryPoints.push(domainTopicPoints);
    });
    return gradeBoundaryPoints;
  }

  @Watch('activeDomain')
  private async watchActiveDomain() {
    this.isExpanding = true;
    if (this.activeDomain) {
      this.resetRouteLines(true);
      const activeDomainData = this.chartData.find(
        (domainData: Domain) => this.activeDomain && (domainData.domainSeq === this.activeDomain.domainSeq));
      await this.handleDomainBarTransition(activeDomainData, activeDomainData.domainSeq, !activeDomainData.isExpanded);
      await this.drawTopicChart(activeDomainData, activeDomainData.domainSeq);
      this.handleDomainGradeTransition(activeDomainData.domainSeq);
      if (this.isShowRouteLine && this.isDemoTenant) {
       setTimeout(() => {
        this.drawRouteLine(this.rgDomains);
       }, 800);
      }
    } else {
      this.chartData.map((domain: Domain) => {
        if (domain.isActive) {
          this.onCloseDomain(domain);
          domain.isExpanded = false;
        }
        domain.isActive = false;
      });
    }
    this.isExpanding = false;
  }

  @Watch('activeTopic')
  private watchActiveTopic() {
    if (this.isDemoTenant && this.activeTopic) {
      this.generateRoute(this.activeTopic);
    }
  }

  @Watch('activeCompetency')
  private onToggleCompetency() {
   if (this.isDemoTenant) {
    this.generateRoute(this.activeCompetency);
   }
   this.highlightCompetency(this.activeCompetency);
  }

  @Watch('month')
  private onChangeTimeline() {
    const component = this;
    component.loadChartData();
  }

  private addDomaniBackShadow() {
    const group = d3.select(this.$el).select('.domains-group')
      .append('defs')
      .append('filter')
      .attr('class', 'domain-shadow');
    group
      .append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '5');
  }

  private parseDomainTopicCompetencyMatrix(domainTopicMatrix: Domain[], domainTopicMetadata: Domain[]) {
    const chartData: any = [];
    this.skylinePoints = [];
    this.numberOfTopics = 0;
    this.maxDomainSize = Math.max.apply(Math, domainTopicMatrix.map((domain) => domain.totalCompetencies || 0));
    let totalCompetencies = 0;
    domainTopicMetadata.map((domainMetadata: Domain) => {
      const crossWalkDomain: any = this.crossWalkData.find((cwDomain: any) => {
          return cwDomain.domainCode === domainMetadata.domainCode;
      });
      const topicSkylinePoints: any[] = [];
      this.numberOfTopics += domainMetadata.topics.length;
      const domainMatrix = domainTopicMatrix.find((matrix: Domain) => matrix.domainCode === domainMetadata.domainCode);
      const parsedDomainData = { ...domainMetadata, ...domainMatrix };
      this.highestTopicSize = this.highestTopicSize < domainMetadata.topics.length ?
        domainMetadata.topics.length :
        this.highestTopicSize;
      if (domainMatrix && domainMatrix.topics) {
        const parsedDomainTopicData: Topic[] = [];
        domainMatrix.topics.map((topicMatrix: Topic) => {
          const topicMetadata = domainMetadata.topics.find((topic: Topic) => topic.topicCode === topicMatrix.topicCode);
          const cwTopic = crossWalkDomain && crossWalkDomain.topics.find(
            (cwTopicItem: any) => cwTopicItem.topicCode === topicMatrix.topicCode);
          if (topicMatrix.competencies) {
            topicMatrix.competencies.map((competency: Competency) => {
              const cwCompetency = cwTopic && cwTopic.competencies.find(
                (cwComp: any) => cwComp.competencyCode === competency.competencyCode);
              totalCompetencies++;
              competency.domainCode = domainMetadata.domainCode,
                competency.domainSeq = domainMetadata.domainSeq,
                competency.topicCode = topicMatrix.topicCode,
                competency.topicSeq = topicMatrix.topicSeq,
                competency.competencyStatus = this.isCompetencyMap ? 2 : competency.status;
              competency.isMastered = competency.status > 1;
              competency.isInferred = competency.status === 2 || competency.status === 3;
              competency.isSkyLineCompetency = false;
              competency.isGradeBoundary = false;
              competency.competencyDisplayCode = cwCompetency ? cwCompetency.frameworkCompetencyDisplayCode : null;
            });
            topicMatrix.competencies[0].isSkyLineCompetency = true;
          }

          topicSkylinePoints.push({
            topicSeq: topicMatrix.topicSeq,
            skylineCompetencySeq: (topicMatrix.masteredCompetencies || 0) + (topicMatrix.inferredCompetencies || 0),
          });
          parsedDomainTopicData.push({ ...topicMatrix, ...topicMetadata });
        });
        this.totalCompetencies = totalCompetencies;
        parsedDomainData.topics = parsedDomainTopicData;
      }
      parsedDomainData.masteredCompetencies = (parsedDomainData.masteredCompetencies || 0)
                                    + (parsedDomainData.inferredCompetencies || 0);
      this.skylinePoints.push({
        domainSeq: parsedDomainData.domainSeq,
        skylineCompetencySeq: parsedDomainData.masteredCompetencies,
        topicSkylinePoints,
      });
      chartData.push(parsedDomainData);
    });
    this.chartData = chartData;
    if (this.activeMarker) {
      this.loadMarkerBoundaries(chartData);
    }
    this.$emit('onloadedChartData', this.chartData);
    this.drawDomainTopicCompetencyChart();
    this.drawGradeBoundaryLine();
  }

  private drawDomainTopicCompetencyChart() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartHeight = component.chartHeight;
    const chartWidth = component.chartWidth;
    component.cellHeight = this.isShowExpandedGraph ? 40 : chartHeight / component.maxDomainSize;
    component.cellWidth = (chartWidth / proficiencyChartData.length) > 200
      ? 200 : (chartWidth / proficiencyChartData.length);
    d3.select(this.$el).select('svg.chart-graph').remove();

    const svg = d3.select(this.$el).select('.chart-area')
      .append('svg')
      .attr('class', 'chart-graph')
      .attr('width', component.cellWidth * proficiencyChartData.length)
      .attr('height', chartHeight);
    svg.append('g').attr('class', 'domains-group');
    component.skylineContainer = svg.append('g').attr('class', 'skyline-group');
    component.gradelineContainer = svg.append('g').attr('class', 'gradeline-group');
    component.routeContainer = svg.append('g').attr('class', 'prerequisite-group');
    component.proficiencyChartContainer = svg;
    this.addDomaniBackShadow();
    if (this.isShowExpandedGraph) {
      this.drawExpandedChart(proficiencyChartData);
    } else {
      proficiencyChartData.map((domainChartData: any, seq: number) => {
        component.drawDomainChart(domainChartData, seq + 1);
      });
    }


    if (!this.isCompetencyMap) {
      component.drawProficiencySkyline();
    }
    if (!this.isSelectedCompetency) {
      const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
      chartContainer.scrollTop = chartHeight;
    }
    component.isLoading = false;
  }

  private drawExpandedChart(proficiencyChartData: any) {
    const svg = d3.select(this.$el).select('.domains-group');
    const cellWidth = this.cellWidth;
    const cellHeight = this.cellHeight;
    let numberOfColumns = 1;
    this.isExpanding = true;
    proficiencyChartData.map((domainData: any) => {
      const domainGroup = svg.append('g').attr('id', `domain-group-${domainData.domainSeq}-${this.subjectClassCode}`);
      domainGroup.attr('transform', `translate(${cellWidth * (numberOfColumns - 1)}, 0)`);
      this.chartData[domainData.domainSeq - 1].isExpanded = true;
      numberOfColumns += domainData.topics.length;
      const domainCompetencyGroup = domainGroup.append('g').attr('class', 'competencies-group')
        .on('click', async () => {
          await this.handleDomainBarTransition(domainData, domainData.domainSeq);
          await this.drawTopicChart(domainData, domainData.domainSeq);
          this.handleDomainGradeTransition(domainData.domainSeq, true);
        });

      this.drawTopicChart(domainData, domainData.domainSeq);
      this.highlightDomain(domainData.domainSeq, true);
    });
    this.drawProficiencySkyline();
    this.toggleChartSize(this.numberOfTopics, true, false);
    this.isExpanding = false;
  }

  private async drawTopicChart(domain: Domain, domainSeq: number) {
    const domainGroup = d3.select(`#domain-group-${domainSeq}-${this.subjectClassCode}`);
    domainGroup.select('.topic-group').remove();
    const topicGroup = domainGroup.append('g').attr('class', `topic-group`);
    let seq = 0;
    for await (const topic of domain.topics) {
      const topicContainer = topicGroup.append('g').attr('class', `topic-${seq + 1}`)
        .attr('transform', `translate(${seq * this.cellWidth}, 0)`);
      if (topic.competencies) {
        const competencyCells = topicContainer.selectAll('.competency').data(topic.competencies);
        competencyCells.enter()
          .append('rect')
          .on('click', (competency: Competency) => {
            this.$emit('onSelectCompetency', competency);
            if (this.activeCompetency === competency && this.isDemoTenant) {
              this.drawRouteLine(this.rgDomains);
            }
            if (this.isCompetencyMap) {
              this.isSelectedCompetency = true;
            }
          })
          .on('keypress', (competency: Competency) => {
            this.$emit('onSelectCompetency', competency);
            if (this.activeCompetency === competency && this.isDemoTenant) {
              this.drawRouteLine(this.rgDomains);
            }
            if (this.isCompetencyMap) {
              this.isSelectedCompetency = true;
            }
          })
          .on('mousemove', (competency: any) => {
            this.competencyHoverData = {
              isShow : true,
              data : competency,
              style: {
                left : (d3.event.pageX + 30) + 'px',
                top : (d3.event.pageY - 30) + 'px',
                display: 'block',
              },
            };
          })
          .on('mouseleave', () => {
            this.competencyHoverData.style.display = 'none';
            this.competencyHoverData.isShow = false;
          })
          .transition()
          .duration(500)
          .delay(200)
          .attr('class', (competency: Competency) => {
            return `competency-cell-${competency.competencySeq}`;
          })
          .attr('tabindex', this.showTabIndex === false ? -1 : 0)
          .attr('role', 'link')
          .attr('aria-label', (competency: any) => {
            return `Competency ${competency.status === 1 ? 'In Progress' : (competency.status === 0 ? 'Not started' : 'Mastered')} `;
          })
          .attr('width', this.cellWidth)
          .attr('height', this.topicCellHeight)
          .attr('x', 0)
          .attr('y', (d: any, i: number) => {
            return i * this.topicCellHeight;
          })
          .attr('class', (competency: any) => {
            return `competency-cell-${competency.competencySeq} competency-cell ${this.isCompetencyMap ? ' competency-status-fill-5 ' : 'competency-status-fill-' + competency.status}
            ${competency.isSkylineCompetency ? 'skyline-competency' : ''}`;
          });
        competencyCells.exit().remove();
      }
      seq++;
    }
  }

  private async handleDomainBarTransition(domain: Domain, domainSeq: number, isExpand: boolean = true) {
    let lastMovingDomain = isExpand ? this.chartData.length : domainSeq + 1;
    const numberOfColumns = domain.topics.length > 1 ? domain.topics.length : 0; // domain seq * no.of topics
    const activeDomainHighlight = 20;
    if (numberOfColumns > 1) {
      await (async () => {
        while ((lastMovingDomain > domainSeq && isExpand) || (!isExpand && lastMovingDomain <= this.chartData.length)) {
        const domainGroup = d3.select(`#domain-group-${lastMovingDomain}-${this.subjectClassCode}`);
        const traslation = this.getTranslation(domainGroup.attr('transform'));
        const xValue = isExpand ?
          traslation[0] + ((numberOfColumns - 1) * this.cellWidth) :
          traslation[0] - ((numberOfColumns - 1) * this.cellWidth);
        const domainPosTo = isExpand ? ((lastMovingDomain - 1) + (numberOfColumns - 1)) : lastMovingDomain - 1;
        // Required while adding gap between domain panel
        const adjustedPos = isExpand ? xValue + activeDomainHighlight : xValue - activeDomainHighlight;
        await domainGroup.transition()
          .duration(this.isCloseOtherDomain ? 0 : 800)
          // .attr('transform', `translate(${this.cellWidth *  domainPosTo}, 0)`);
          .attr('transform', `translate(${xValue}, 0)`);
        isExpand ? lastMovingDomain-- : lastMovingDomain++;

      }})();
    }
    this.toggleChartSize(numberOfColumns, isExpand);
    this.toggleDomainGroup(domainSeq, isExpand);
    this.highlightDomain(domainSeq, isExpand);
  }

  private highlightDomain(domainSeq: number, isExpand: boolean) {
    const domainGroup = d3.select(`#domain-group-${domainSeq}-${this.subjectClassCode}`);
    domainGroup.attr('class', isExpand ? 'active' : '');
    const chartContainer = this.$el.querySelector('.scrollable-chart') as HTMLElement;
    if (isExpand) {
      domainGroup.append('foreignObject')
        .attr('class', 'domain-highlight')
        .attr('width', (this.chartData[domainSeq - 1].topics.length * this.cellWidth) + 'px')
        .attr('height', this.chartHeight + 'px');
    } else {
      domainGroup.select('foreignObject').remove();
    }

  }

  private toggleChartSize(numberOfColumns: number = 0, isExpand: boolean, isAdjust: boolean = true) {
    if (numberOfColumns > 1) {
      const chartWidth = parseFloat(d3.select('.chart-graph').attr('width'));
      const adjustableWidth = this.cellWidth * (numberOfColumns - 1);
      let updatedWidth = adjustableWidth;
      if (isAdjust) {
        updatedWidth = isExpand ? chartWidth + adjustableWidth : chartWidth - adjustableWidth;
      }
      d3.select('.chart-graph').attr('width', updatedWidth);
    }
  }


  private toggleDomainGroup(domainSeq: number, isExpand: boolean) {
    const domainGroup = d3.select(`#domain-group-${domainSeq}-${this.subjectClassCode}`);
    this.chartData.map((domain: Domain) => domain.isActive = false);
    if (isExpand) {
      domainGroup.select('.competencies-group').attr('class', 'competencies-group hidden');
      domainGroup.select('.topic-group').attr('class', 'topic-group show');
      this.skylinePoints[domainSeq - 1].isExpanded = true;
      this.chartData[domainSeq - 1].isExpanded = true;
      this.chartData[domainSeq - 1].isActive = true;
    } else {
      domainGroup.select('.competencies-group').attr('class', 'competencies-group show');
      domainGroup.select('.topic-group').attr('class', 'topic-group hidden');
      this.skylinePoints[domainSeq - 1].isExpanded = false;
      this.chartData[domainSeq - 1].isExpanded = false;
    }
    if (isExpand) {
      // Scroll to active domain
      const scrollElement: any = this.$el.querySelector('.graph-visual');
      const scrollDistance = (domainSeq - 1) * this.cellWidth;
      setTimeout(() => scrollElement.scrollLeft = scrollDistance , 10);
    }
    this.drawProficiencySkyline();
  }

  private onClearGrade() {
    d3.select(this.$el).select('.gradeline-group').remove();
  }

  private drawProficiencySkyline() {
    const component = this;
    const skylineElements: any = component.$el.querySelectorAll('.skyline-competency');
    const cellWidth = component.cellWidth;
    const cellHeight = component.cellHeight;

    let points = '';

    d3.select(this.$el).select('.skyline-group polyline').remove(); // Remove skyline, if exist

    let curBarPos = 0;
    let strokeDash = 0;
    let adjustableWidth = 10;
    this.skylinePoints.forEach((domain: any) => {
      if (domain.isExpanded) {
        // Get topic bar competency pos
        adjustableWidth = 10;
        domain.topicSkylinePoints.map((topic: any, index: number) => {
          let end = 0;
          if (index === domain.topicSkylinePoints.length - 1) {
            end = 10;
          }
          const x1 = curBarPos * this.cellWidth;
          const x2 = x1 + this.cellWidth;
          const y1 = topic.skylineCompetencySeq * this.topicCellHeight;
          const y2 = y1;


          points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
          curBarPos++;
          strokeDash += (x1 === x2
            ? Math.max(y1, y2)
            : Math.max(x1, x2));
        });
      } else {
        const x1 = curBarPos * this.cellWidth;
        const x2 = x1 + this.cellWidth;
        const y1 = domain.skylineCompetencySeq * this.cellHeight;
        const y2 = y1;
        strokeDash += (x1 === x2
          ? Math.max(y1, y2)
          : Math.max(x1, x2));
        points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
        curBarPos++;
      }
    });

    const skylineContainer = component.skylineContainer;

    d3.select(this.$el).select('.skyline-group')
      .append('polyline')
      .attr('points', points)
      .attr('class', 'skyline')
      .attr('stroke-dasharray', strokeDash)
      .attr('stroke-dashoffset', strokeDash);
    component.addSkylineBackshadow();
  }

  private handleDomainGradeTransition(domainSeq?: number, isExpand: boolean = true) {
    const activatedGradeBoundaries = this.fetchedGradeBoundaries.filter((gradeBoundaries) => gradeBoundaries.isActive);
    const expandedDomainsSeq: any[] = [];
    this.chartData.map((data: Domain) => {
      if (data.isExpanded) {
        expandedDomainsSeq.push(data.domainSeq);
      }
    });
    activatedGradeBoundaries.map((gradeBoundaries) => {
      gradeBoundaries.boundaryPoints.map((boundaryPoints) => {
        if ((domainSeq && domainSeq === boundaryPoints.domainSeq)
                    || !domainSeq || expandedDomainsSeq.includes(boundaryPoints.domainSeq)) {
          boundaryPoints.isExpanded = isExpand;
        }
      });
    });
    this.drawGradeBoundaryLine();
  }

  private toggleActiveDomainBar(seq: number, isActive: boolean = true) {
    const component = this;
    component.domainTopicMetadata.forEach((domainCoOrdinate: any) => {
      const domainBar: any = component.$el.querySelector(`#domain-group-${domainCoOrdinate.domainSeq}-${this.subjectClassCode}`);
      domainBar.classList.remove('non-active', 'active');
      if (isActive) {
        domainBar.classList.add('non-active');
      }
    });
    if (seq > 0) {
      const activeDomainBar: any = component.$el.querySelector(`#domain-group-${seq}-${this.subjectClassCode}`);
      activeDomainBar.classList.add('active');
      activeDomainBar.classList.remove('non-active');
    }
  }

  private highlightCompetency(competency: Competency) {
    const component = this;
    if (competency) {
      const competencyContainer: any = component.$el.querySelector(`
      #domain-group-${competency.domainSeq}-${this.subjectClassCode} .topic-${competency.topicSeq}
      .competency-cell-${competency.competencySeq}`) as HTMLElement;
      d3.select(`#domain-group-${competency.domainSeq}-${this.subjectClassCode} #active-competency`).remove();
      d3.select(`#domain-group-${competency.domainSeq}-${this.subjectClassCode}`)
        .append('g')
        .attr('id', 'active-competency')
        .attr('transform', `translate(${(competency.topicSeq ? competency.topicSeq - 1 : 0) * this.cellWidth}, ${competencyContainer.getAttribute('y')})`)
        .append('foreignObject')
        .attr('class', `active-competency competency-status-fill-${competency.competencyStatus}`)
        .attr('tabindex', this.showTabIndex === false ? -1 : 0)
        .attr('role', 'link')
        .attr('aria-label', `Active Competency ${competency.competencyStatus === 1 ? 'In Progress' : (competency.competencyStatus === 0 ? 'Not started' : 'Mastered')}`)
        .attr('width', this.cellWidth)
        .attr('height', this.topicCellHeight);
    }
  }

  private isActiveGradeList(value: any) {
    return this.activeGradeList.find((grade: any) => grade.id === value.id) ||
      (this.activeGradeList.length > 1 &&
        this.activeGradeList[0].id > value.id && this.activeGradeList[1].id < value.id);
  }

  private drawPrerequisitesLine() {
    const prerequisiteElement: any = this.$el.querySelectorAll('.prerequisite-content');
    const cellWidth = this.cellWidth;
    const cellHeight = this.cellHeight;
    const routeContainer = this.routeContainer;
    const linePoints: any = [];
    prerequisiteElement.forEach((element: any, elementIndex: number) => {
      const x1 = parseInt(element.getAttribute('x'), 10);
      const y1 = parseInt(element.getAttribute('y'), 10);
      linePoints.push({
        x: (x1 + (cellWidth / 2)),
        y: (y1 + (cellHeight / 2)),
      });
      routeContainer
        .append('circle')
        .attr('class', 'pre-connect')
        .attr('cx', (x1 + (cellWidth / 2)))
        .attr('cy', (y1 + (cellHeight / 2)))
        .attr('r', 5)
        .style('fill', '#fff');
    });
    const line = d3.line()
      .x((d: any) => d.x)
      .y((d: any) => d.y)
      .curve(d3.curveLinear);

    routeContainer.append('path')
      .attr('d', line(linePoints))
      .attr('stroke', '#fff')
      .attr('stroke-width', 4)
      .attr('fill', 'none');

  }

  private resetChart() {
    const proficiencyChartData = this.chartData;
    proficiencyChartData.map((domainData: any) => {
      const competencies = domainData.competencies;
      const filteredDomain = competencies.filter(
        (competency: any) => (competency.className && competency.className !== '')
          || competency.isLowline || competency.isNoMapping || competency.isGradeBoundary);
      filteredDomain.map((filteredCompetency: any) => {
        filteredCompetency.className = '';
        filteredCompetency.isLowline = false;
        filteredCompetency.isNoMapping = false;
        filteredCompetency.isGradeBoundary = false;
      });
    });
  }

  private minGradeLine() {
    return new Promise((resolve, reject) => {
      const minGrade = this.activeGradeList.reduce(
        (prev: any, current: any) => (prev.sequence < current.sequence) ? prev : current);
      const minGradeIndex = this.taxonomyGrades.findIndex((grade) => grade.id === minGrade.id);
      const prevGrade: any = minGradeIndex ? this.taxonomyGrades[minGradeIndex - 1] : null;
      if (prevGrade) {
        this.minGradeNumber = prevGrade;
      } else {
        return resolve;
      }
    });
  }

  private maxGradeClass(gradeIndex: any) {
    return (this.isCompetencyMap) ?
      (gradeIndex ? (((this.maxGradeNumber.id === gradeIndex) ?
        'multi-grade-hiline' : 'multi-grade')) : 'low-line') : '';
  }

  private clearExisitingDomain(domain: any) {
    this.isCloseOtherDomain = true;
    return new Promise((resolve) => {
        setTimeout(() => {
          resolve(this.onCloseDomain(domain));
        }, 50);
    });
  }

  private generateRoute(selectedItem: Competency | any) {
    this.rgDomains  = [];
    this.resetRouteLines();
    const params: any = {
      topicCode: selectedItem.topicCode,
      userId: this.userId,
      subjectId: this.subject.id,
      queryParams: {
        domain: selectedItem.domainCode,
        fwCode: 'GUT',
      },
    };
    if (selectedItem.competencyCode) {
      params.queryParams.competency = selectedItem.competencyCode;
    }
    competencyAPI.generateRoute(params).then((routeSubjects: RGSubjectModel[]) => {
      const activeSubject: any = routeSubjects.find((subject) => subject.subjectCode === this.subject.id);
      if (activeSubject && this.isDemoTenant) {
        this.rgDomains = activeSubject.domains;
      }
      this.$emit('onSelectRoute', this.rgDomains);
    });
  }

  private drawRouteLine(rgDomains: RGDomainModel[]) {
      this.resetRouteLines();
      const chartData: Domain[] = this.chartData;
      const routeDomains: RGDomainModel[] = rgDomains;
      const linePoints: any = [];
      let cursPosition = 0;
      const maxRGSeq = this.getHighestSeq(routeDomains);
      const compCellHight = this.compressedGraphCellHeight;
      const routeContainer = this.routeContainer;
      const directionContainer = routeContainer.append('g').attr('id', 'direction-container');
      const circleContainer = routeContainer.append('g').attr('id', 'circle-container');
      const radius = 4;
      d3.selectAll('.competency-cell').classed('fade-competency', true);
      this.isShowRouteLine = true;
      chartData.forEach((domain) => {
        const rgDomain: RGDomainModel | any = routeDomains.find(
            (rgdItem) => rgdItem.domainCode === domain.domainCode);
        if (rgDomain) {
          rgDomain.domainName = domain.domainName;
          rgDomain.domainSeq = domain.domainSeq;
          domain.topics.forEach((topic) => {
            let competencyCount = 0;
            const rgTopic: RGTopicModel = rgDomain!.topics.find(
              (rgTopicItem: RGTopicModel) => rgTopicItem.topicCode === topic.topicCode);
            if (rgTopic) {
              rgTopic.topicName = topic.topicName;
              rgTopic.topicSeq = topic.topicSeq;
              rgTopic.domainName = domain.domainName;
              rgTopic.domainCode = domain.domainCode;
              topic.competencies!.forEach((competency) => {
                const rgCompetency: any = rgTopic!.competency.find(
                  (rgComp: RGCompetencyModel) => rgComp.competencyCode === competency.competencyCode);
                if (rgCompetency) {
                  rgCompetency.domainName = domain.domainName;
                  rgCompetency.domainSeq = domain.domainSeq;
                  rgCompetency.topicName = topic.topicName;
                  rgCompetency.topicSeq = topic.topicSeq;
                  rgCompetency.domainCode = domain.domainCode;
                  rgCompetency.topicCode = topic.topicCode;
                  rgCompetency.competencyStatus = competency.competencyStatus;
                  if (this.activeCompetency.competencyCode === competency.competencyCode) {
                    this.activeCompetency.topicName = topic.topicName;
                    this.activeCompetency.domainName = domain.domainName;
                    this.activeCompetency.competencyDisplayCode = rgCompetency.competencyDisplayCode;
                  }
                  const cellHeight = domain.isExpanded ? this.topicCellHeight : this.chartHeight / this.maxDomainSize;
                  const cellWidth = this.cellWidth;
                  const points = {
                    x: ((cursPosition * cellWidth) + cellWidth / 2),
                    y: (competencyCount * cellHeight) + (cellHeight / 2),
                    sequence: rgCompetency ? rgCompetency.competencySeq : maxRGSeq,
                    isExpanded: domain.isExpanded ? true : false,
                    domainSeq: domain.domainSeq,
                  };
                  linePoints.push(points);
                  d3.select(`#domain-group-${domain.domainSeq}-${this.subjectClassCode} .topic-${topic.topicSeq} .competency-cell-${competency.competencySeq}`)
                    .classed('fade-competency', false);
                }

                competencyCount++;
              });
            }
            if (domain.isExpanded) { cursPosition++; }
          });
        }
        if (!domain.isExpanded) { cursPosition++; }
        if (!rgDomain && domain.isExpanded) { cursPosition = cursPosition + domain.topics.length; }
      });
      sortByProperty(linePoints, 'sequence', 'ASC');
      this.loadMapRoutePath(linePoints);
  }

  private resetRouteLines(softReset = false) {
    const routeContainer = this.routeContainer;
    if (routeContainer) {
      routeContainer.selectAll('*').remove();
    }
    d3.selectAll('.competency-cell').classed('fade-competency', false);
    if (!softReset) {
      this.isShowRouteLine = false;
    }
  }


  private fetchCrosswalk() {
    const params = {
      subjectCode: this.subjectCode,
      fwCode: 'GUT',
    };
    return taxonomyAPI.fetchCrosswalk(params);
  }

  private getHighestSeq(domains: RGDomainModel[]) {
    let maxSeq = 0;
    domains.forEach((domain) => {
      domain.topics.forEach((topic) => {
        topic.competency.forEach((competency) => {
            if (competency.competencySeq > maxSeq) {
              maxSeq = competency.competencySeq + 1;
            }
        });
      });
    });
    return maxSeq;
  }

  private loadMapRoutePath(linepoints: any) {
      const directionContainer = this.routeContainer.select('#direction-container');
      const imageWidth = 50;
      const imageHeight = 50;
      const findNearPoints = (points: any, i: number, nodes: any) => {
        let seq: any = i + 1;
        const nearPoints = linepoints.filter((nPoints: any) =>  {
          return (!points.isExpanded &&
                  points.domainSeq === nPoints.domainSeq &&
                  points.y + 5 > nPoints.y && points.y - 5 < nPoints.y);
        });
        if (nearPoints.length > 1) {
          seq = nearPoints[0] === points ? seq : '';
          if (nearPoints[0] === points) {
            d3.select(nodes[i].parentNode)
                .append('text')
                .attr('dx', 15)
                .attr('dy', 5)
                .attr('class', 'text-box small-text')
                .text(`+${nearPoints.length - 1}`);
          }
        }
        return seq;
      };
      const pathSeq = directionContainer.selectAll('path-seq')
                        .data(linepoints)
                        .enter()
                        .append('g')
                        .attr('class', 'path-seq')
                        .attr('transform', (d: any) => {
                          return `translate(${d.x}, ${d.y}) rotate(180)`;
                        });

      pathSeq.append('text')
                          .attr('dy', 5)
                          .attr('class', 'text-box')
                          .text(findNearPoints);

      const firstChild = directionContainer.select('.path-seq:first-child');
      firstChild.select('text')
                         .attr('dy', 10);

      firstChild.append('image')
                          .attr('x', -(imageWidth / 2))
                          .attr('y', -imageHeight)
                          .attr('xlink:href', ROUTE_MAP_ICONS.startPoint)
                          .attr('width', imageWidth)
                          .attr('height', imageHeight);

      const lastChild = directionContainer.select('.path-seq:last-child');
      lastChild.select('text')
                        .attr('dy', 10);
      lastChild.append('image')
                        .attr('x', -(imageWidth / 2))
                        .attr('y', -imageHeight)
                        .attr('xlink:href', ROUTE_MAP_ICONS.destination)
                        .attr('width', imageWidth)
                        .attr('height', imageHeight);
  }

  private loadMarkerBoundaries(chartData: any) {
    const activeMarker = this.activeMarker;
    if (activeMarker) {
      const boundaries = markerSerilaizer.normalizeMarkerHighline(chartData, activeMarker);
      const markerBoundaryPoints = this.getGradeBoundaryPoints(boundaries);
      this.fetchedGradeBoundaries = [{
        grade: activeMarker.id,
        boundaries,
        isActive: true,
        boundaryPoints: markerBoundaryPoints,
      }];
    }
    this.drawGradeBoundaryLine();
  }

}
